import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ResourceType } from '@cotera/api';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import type { ResourceAccessEntry } from '@cotera/client/app/etc/api/typed-client';

export type ChatHeaderMeta = {
  /** True once the chat has been archived — it still opens, it just isn't live. */
  isArchived: boolean;
  /** Whoever created the chat, excluded from the "shared with" stack. */
  ownerId: string | null;
  /** Explicit user grants, owner included; filter before display. */
  users: ResourceAccessEntry[];
  /** Whether the whole org can see this chat. */
  sharedWithOrg: boolean;
};

const EMPTY: ChatHeaderMeta = {
  isArchived: false,
  ownerId: null,
  users: [],
  sharedWithOrg: false,
};

/**
 * The two things the chat header needs about a chat that the conversation
 * itself doesn't carry: whether it is archived, and who it is shared with.
 *
 * Reads `resource.get` on the shared per-resource key, so this is free whenever
 * anything else on the page has already fetched the chat (the breadcrumb's
 * folder resolution, most often) and costs one cached request otherwise.
 *
 * The result is memoized on the query data, and has to be. It reaches the
 * artifact panel as `PanelHost.accessControl`, which `PanelUnifiedChrome` lists
 * in its own memo's deps and publishes as a context value — so a fresh object
 * here re-renders every consumer of that context. The chat page re-renders on
 * each message chunk (it subscribes to `messagesWatchable`), which would make
 * that the whole panel's chrome, per token of every streamed reply.
 */
export function useChatHeaderMeta(conversationId: string): ChatHeaderMeta {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);

  const { data } = useQuery({
    queryKey: queryKeys.resource.get({
      orgId,
      type: ResourceType.Chat,
      id: conversationId,
    }),
    queryFn: () =>
      client.resource.get({ type: ResourceType.Chat, id: conversationId }),
    enabled: conversationId !== '',
    staleTime: 60_000,
  });

  return useMemo((): ChatHeaderMeta => {
    if (data === undefined || data.isErr()) {
      return EMPTY;
    }
    const value = data.value;
    if (value.type !== 'chat') {
      return EMPTY;
    }

    const users = value.meta?.users ?? [];
    return {
      isArchived: value.chat.status === 'archived',
      ownerId: value.chat.actorId,
      users,
      // Only a grant to this org is "everyone at <this org>".
      sharedWithOrg: users.some(
        (entry) =>
          entry.t === 'org' &&
          (entry.orgId === undefined || entry.orgId === orgId)
      ),
    };
  }, [data, orgId]);
}
